import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Wallet as WalletIcon, Gift, Sparkles, Zap, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { useAuth } from "../AuthContext";
import { api } from "../api";
import { t } from "../i18n";
import { formatPhone } from "../utils/phone";
import ShareBalanceCard from "../components/ShareBalanceCard";
import {
  trackOrderCreated,
  trackSignatureReceived,
  clearOrder,
  verifyWithRetry,
  runRechargeRecovery,
} from "../lib/rechargeRecovery";

const RZP_SCRIPT = "https://checkout.razorpay.com/v1/checkout.js";

/** Loads the Razorpay Checkout script once (idempotent). */
function loadRazorpay() {
  return new Promise((resolve) => {
    if (typeof window === "undefined") return resolve(false);
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = RZP_SCRIPT;
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

function StatusIcon({ status }) {
  if (status === "paid") return <CheckCircle2 size={18} className="text-emerald-500" strokeWidth={2.5} />;
  if (status === "cancelled" || status === "signature_failed") return <XCircle size={18} className="text-rose-500" strokeWidth={2.5} />;
  return <Loader2 size={18} className="text-slate-400" strokeWidth={2.5} />;
}

export default function Wallet() {
  const navigate = useNavigate();
  const { user, lang, config, updateBalance } = useAuth();
  const balance = user?.wallet_balance ?? 0;
  const currency = config?.currency_symbol || "₹";
  const freeCredit = config?.free_wallet_credit ?? 100;
  const options = config?.recharge_options || [50, 100, 200, 500];
  const enabled = !!config?.recharge_enabled;
  const keyId = config?.razorpay_key_id;

  const [selected, setSelected] = useState(options[1] || 100);
  const [paying, setPaying] = useState(false);
  const [history, setHistory] = useState([]);

  const loadHistory = async () => {
    try {
      const r = await api.get("/wallet/transactions");
      setHistory(r.data?.items || []);
    } catch (_e) { /* silent */ }
  };

  useEffect(() => { loadHistory(); }, []);

  // Crash-safe recovery: on every Wallet mount, replay any pending orders.
  // This handles the rare "app died after payment success but before verify" case.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const outcomes = await runRechargeRecovery({
          onCredited: ({ amount, balance }) => {
            if (!alive) return;
            updateBalance(balance);
            toast.success(`Recovered payment — ${currency}${amount} added! Balance ${currency}${balance}.`);
            loadHistory();
          },
        });
        if (alive && outcomes.length > 0) {
          console.info("[recharge] recovery outcomes", outcomes);
        }
      } catch (e) {
        console.error("[recharge] recovery threw", e);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRecharge = async () => {
    if (paying || !enabled || !keyId) {
      toast.error("Recharge is currently unavailable. Please try again later.");
      return;
    }

    setPaying(true);
    console.info("[recharge] payment initiated", { amount: selected, uid: user?.id });
    const sdkOk = await loadRazorpay();
    if (!sdkOk) {
      setPaying(false);
      toast.error("Couldn't load payment SDK. Check your internet & try again.");
      return;
    }

    let order;
    try {
      const r = await api.post("/wallet/recharge/create-order", { amount: selected });
      order = r.data;
      console.info("[recharge] order created", { order_id: order.order_id, amount: order.amount });
      // Persist BEFORE opening Razorpay — survives any subsequent app crash.
      trackOrderCreated({ order_id: order.order_id, amount: selected, user_id: user?.id });
    } catch (e) {
      setPaying(false);
      console.error("[recharge] create-order failed", e?.response?.data || e?.message);
      toast.error(e.response?.data?.detail || "Could not start payment");
      return;
    }

    const rzp = new window.Razorpay({
      key: order.key_id,
      amount: order.amount,
      currency: order.currency,
      order_id: order.order_id,
      name: "Saral Eye",
      description: `Wallet recharge ${currency}${selected}`,
      image: "/icon-512.png",
      theme: { color: "#f472b6" },
      // Defensive Razorpay options: never let the SDK redirect the whole page
      // (kills our React state) and never let it pop its own retry prompt on
      // failure (its overlay sometimes blocks our DOM on Android WebViews).
      redirect: false,
      retry: { enabled: false },
      prefill: {
        contact: user?.phone ? (String(user.phone).startsWith("+") ? user.phone : `+91${user.phone}`) : undefined,
      },
      notes: { user_id: user?.id, phone: user?.phone },
      modal: {
        escape: true,
        confirm_close: false,
        ondismiss: async () => {
          // User closed the Razorpay modal without paying. NB: do NOT clear the
          // pending order here — if they actually paid right before tapping
          // close, recovery will still rescue it via reconcile.
          setPaying(false);
          console.info("[recharge] modal dismissed", { order_id: order.order_id });
          try { await api.post("/wallet/recharge/cancel", { amount: selected }); } catch (_e) { /* ignore */ }
          try { toast.message("Payment cancelled"); } catch (_e) { /* ignore */ }
          try { loadHistory(); } catch (_e) { /* ignore */ }
          // Best-effort: kick off a recovery in case payment did complete.
          setTimeout(() => {
            runRechargeRecovery({
              onCredited: ({ amount, balance }) => {
                try { updateBalance(balance); } catch (_e) { /* ignore */ }
                try { toast.success(`Payment confirmed — ${currency}${amount} added!`); } catch (_e) { /* ignore */ }
                try { loadHistory(); } catch (_e) { /* ignore */ }
              },
            }).catch(() => { /* ignore */ });
          }, 1500);
        },
      },
      handler: (resp) => {
        // CRITICAL: this is the success callback. It MUST be crash-proof —
        // Razorpay's Checkout iframe will be torn down the moment we return,
        // and on memory-constrained Android WebViews any uncaught exception
        // here can kill the entire app (the symptom the user reported:
        // "app closes immediately after payment success").
        //
        // Strategy:
        //   1. Persist the signature triple to localStorage (sync, very fast).
        //   2. Show an immediate optimistic toast so the user knows it worked
        //      even if our React tree dies in the next 200 ms.
        //   3. Schedule verify in a *separate* macrotask (not on Razorpay's
        //      callback stack) so a verify failure can never propagate back
        //      into Razorpay's teardown code.
        //   4. Wrap EVERY side-effect in its own try/catch — a single bad
        //      operation (localStorage quota, sonner unmount, etc.) must not
        //      cascade.
        try {
          console.info("[recharge] payment success callback", {
            order_id: resp.razorpay_order_id,
            payment_id: resp.razorpay_payment_id,
          });
        } catch (_e) { /* ignore */ }
        try {
          trackSignatureReceived({
            order_id: resp.razorpay_order_id,
            payment_id: resp.razorpay_payment_id,
            signature: resp.razorpay_signature,
          });
        } catch (persistErr) {
          console.error("[recharge] persist failure (continuing)", persistErr);
        }
        // Optimistic UI — show success NOW so the user has visual confirmation
        // even if the verify roundtrip or the React render later crashes.
        try { toast.success(`Payment received — ${currency}${selected} crediting…`); } catch (_e) { /* ignore */ }

        // Detach verify from the Razorpay callback stack so the SDK can tear
        // down its iframe cleanly before we trigger any React state updates.
        setTimeout(() => {
          (async () => {
            try {
              const v = await verifyWithRetry({
                order_id: resp.razorpay_order_id,
                payment_id: resp.razorpay_payment_id,
                signature: resp.razorpay_signature,
              });
              const d = v.data || {};
              console.info("[recharge] verify success", d);
              if (typeof d.balance === "number") {
                try { updateBalance(d.balance); } catch (_e) { /* ignore */ }
              }
              try { clearOrder(resp.razorpay_order_id); } catch (_e) { /* ignore */ }
              try {
                toast.success(
                  d.already_processed
                    ? `Already credited. Balance ${currency}${d.balance}.`
                    : `${currency}${d.credited} added! New balance ${currency}${d.balance}.`
                );
              } catch (_e) { /* ignore */ }
              try { loadHistory(); } catch (_e) { /* ignore */ }
              // Safety: ensure user is on /wallet so they see updated balance.
              try {
                if (window.location.pathname !== "/wallet") {
                  navigate("/wallet", { replace: true });
                }
              } catch (_e) { /* ignore */ }
            } catch (e) {
              console.error("[recharge] verify failed after retries", e?.response?.data || e?.message);
              // Last-ditch: ask the server to reconcile directly with Razorpay.
              try {
                const r2 = await api.post("/wallet/recharge/reconcile", {
                  razorpay_order_id: resp.razorpay_order_id,
                });
                const d2 = r2.data || {};
                console.info("[recharge] reconcile success after verify failure", d2);
                if (typeof d2.balance === "number") {
                  try { updateBalance(d2.balance); } catch (_e) { /* ignore */ }
                }
                if (d2.success && (d2.credited > 0 || d2.already_processed)) {
                  try { clearOrder(resp.razorpay_order_id); } catch (_e) { /* ignore */ }
                  try { toast.success(`${currency}${d2.credited || 0} added! Balance ${currency}${d2.balance}.`); } catch (_e) { /* ignore */ }
                  try { loadHistory(); } catch (_e) { /* ignore */ }
                } else {
                  try { toast.message("Payment received. We'll credit your wallet shortly."); } catch (_e) { /* ignore */ }
                }
              } catch (e2) {
                console.error("[recharge] reconcile also failed", e2?.response?.data || e2?.message);
                // Keep the entry in the queue — it will be retried on next app load.
                try { toast.error("Payment received. Reopen the app — we'll auto-credit your wallet."); } catch (_e) { /* ignore */ }
              }
            } finally {
              try { setPaying(false); } catch (_e) { /* ignore */ }
            }
          })().catch((finalErr) => {
            // Absolutely-last-resort: an error escaped both try blocks above.
            // Log it but never propagate to the runtime.
            console.error("[recharge] verify chain unhandled error", finalErr);
            try { setPaying(false); } catch (_e) { /* ignore */ }
          });
        }, 0);
      },
    });

    try {
      rzp.on("payment.failed", (resp) => {
        try { setPaying(false); } catch (_e) { /* ignore */ }
        const reason = resp?.error?.description || resp?.error?.reason || "Payment failed";
        console.warn("[recharge] payment.failed", resp?.error);
        try { toast.error(reason); } catch (_e) { /* ignore */ }
        // Don't clear the queue — payment_failed events sometimes precede an
        // actual capture; recovery's reconcile will resolve the true state.
        try { loadHistory(); } catch (_e) { /* ignore */ }
      });
    } catch (_e) { /* ignore */ }

    try {
      rzp.open();
    } catch (openErr) {
      setPaying(false);
      console.error("[recharge] rzp.open() failed", openErr);
      toast.error("Couldn't open payment screen. Please retry.");
    }
  };

  return (
    <div className="page-shell" data-testid="wallet-page">
      <div className="flex items-center justify-between mb-4">
        <button
          data-testid="back-btn"
          onClick={() => navigate("/")}
          className="p-3 rounded-full bg-white border-2 border-slate-900/10 btn-squish"
        >
          <ArrowLeft size={22} strokeWidth={2.5} className="text-slate-700" />
        </button>
        <h2 className="font-heading text-2xl font-bold text-white drop-shadow-[0_3px_10px_rgba(15,23,42,0.55)]">{t(lang, "wallet")}</h2>
        <div className="w-12" />
      </div>

      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-gradient-to-br from-emerald-400 to-sky-400 rounded-3xl p-8 text-white border-4 border-white shadow-[0_10px_0_rgba(0,0,0,0.12)]"
      >
        <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider opacity-90">
          <WalletIcon size={18} strokeWidth={2.5} /> {t(lang, "balance")}
        </div>
        <div className="mt-2 font-heading text-6xl font-bold" data-testid="wallet-page-balance">
          {currency}{balance}
        </div>
        <div className="mt-1 text-sm font-bold opacity-90">{formatPhone(user?.phone)}</div>
      </motion.div>

      {/* Recharge */}
      <div
        data-testid="recharge-section"
        className="mt-6 bg-white rounded-3xl p-5 border-4 border-slate-900/5 shadow-[0_6px_0_rgba(0,0,0,0.05)]"
      >
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-pink-400 flex items-center justify-center">
            <Zap size={26} strokeWidth={2.5} className="text-white" />
          </div>
          <div className="flex-1">
            <h3 className="font-heading text-xl font-bold text-slate-800">{t(lang, "recharge_wallet")}</h3>
            <p className="text-xs text-slate-500 font-semibold">{t(lang, "recharge_methods")}</p>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-4 gap-2.5">
          {options.map((amt) => (
            <button
              key={amt}
              data-testid={`recharge-amount-${amt}`}
              onClick={() => setSelected(amt)}
              className={`relative font-heading font-bold py-3 rounded-2xl border-4 transition-all active:translate-y-0.5 ${
                selected === amt
                  ? "bg-pink-400 text-white border-pink-500 shadow-[0_4px_0_rgba(0,0,0,0.12)]"
                  : "bg-white text-slate-700 border-slate-900/10 hover:border-pink-300"
              }`}
            >
              <span className="text-xs opacity-80">{currency}</span>
              <span className="text-xl ml-0.5">{amt}</span>
            </button>
          ))}
        </div>

        <button
          data-testid="recharge-pay-btn"
          onClick={handleRecharge}
          disabled={paying || !enabled}
          className="mt-4 w-full bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-300 text-white font-heading text-lg font-bold uppercase tracking-wide py-4 rounded-2xl btn-squish flex items-center justify-center gap-2 shadow-[0_4px_0_rgba(0,0,0,0.12)]"
        >
          {paying ? (
            <>
              <Loader2 size={20} strokeWidth={3} className="animate-spin" /> {t(lang, "processing")}
            </>
          ) : (
            <>
              {t(lang, "pay_prefix")} {currency}{selected}
            </>
          )}
        </button>

        {!enabled && (
          <p className="mt-2 text-center text-xs text-rose-500 font-semibold">
            {t(lang, "recharge_unavailable")}
          </p>
        )}
      </div>

      {/* Share Balance (P2P transfer between two users) */}
      <ShareBalanceCard />

      {/* Free credit info */}
      <div className="mt-4 bg-white rounded-3xl p-5 border-4 border-slate-900/5 shadow-[0_6px_0_rgba(0,0,0,0.05)] flex items-start gap-4">
        <div className="w-12 h-12 rounded-2xl bg-yellow-300 flex items-center justify-center shrink-0">
          <Gift size={26} strokeWidth={2.5} className="text-slate-800" />
        </div>
        <div className="flex-1">
          <h3 className="font-heading text-lg font-bold text-slate-800">{t(lang, "free_credit_title").replace("{amount}", `${currency}${freeCredit}`)}</h3>
          <p className="text-sm text-slate-600 font-semibold mt-1">
            {t(lang, "free_credit_desc").replace("{amount}", `${currency}${freeCredit}`)}
          </p>
        </div>
      </div>

      {/* History */}
      {history.length > 0 && (
        <div data-testid="recharge-history" className="mt-4 bg-white rounded-3xl p-4 border-4 border-slate-900/5 shadow-[0_6px_0_rgba(0,0,0,0.05)]">
          <h3 className="font-heading text-base font-bold text-slate-800 px-1 mb-2">{t(lang, "recent_recharges")}</h3>
          <ul className="divide-y divide-slate-100">
            {history.slice(0, 5).map((tx) => (
              <li key={tx.id} className="flex items-center gap-3 py-2.5">
                <StatusIcon status={tx.status} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-slate-800">
                    {currency}{tx.amount_inr}
                    <span className={`ml-2 text-[10px] font-bold uppercase tracking-wider ${
                      tx.status === "paid" ? "text-emerald-600"
                      : tx.status === "cancelled" || tx.status === "signature_failed" ? "text-rose-500"
                      : "text-slate-400"
                    }`}>{tx.status}</span>
                  </div>
                  <div className="text-[11px] text-slate-500 font-semibold truncate">
                    {new Date(tx.created_at).toLocaleString()} · {tx.order_id}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="h-6" />
    </div>
  );
}
